import React, { useEffect, useState, useCallback } from 'react'
import {
  cancelTransactionRequest,
  fetchAccountHistory
} from './cl_AccountHistorySlice'
import { useDispatch, useSelector } from 'react-redux'
import icons from '../../assets/images/sprite-other-icons.svg'
import moment from 'moment'
import PaginationComponent from '../common/PaginationComponent'
import { fetchBonusHistory } from '../bonusHistory/cl_BonusHistorySlice'
import { fetchAccountDetails } from '../accountDetails/cl_AccountDetailsSlice'
import { getFromLocalStorage } from '../../services/helper/cl_Helper'

const AccountHistory = () => {
  const PageSize = 10
  const token = getFromLocalStorage('gk')
  const [currentPage, setCurrentPage] = useState(1)
  const dispatch = useDispatch()
  
  const { accountHistoryData, totalRecords, loader } = useSelector(state => state?.accountHistory)
  const { bonusHistoryData, bonusLoader } = useSelector(state => state?.bonusHistory)
  const { accountDetailsData } = useSelector(state => state?.accountDetails)
  
  const [isCancel, setIsCancel] = useState(false)
  const [selectedPaymentType, setSelectedPaymentType] = useState('DEPOSIT')
  const [selectedDurationType, setSelectedDurationType] = useState('last24hrs')
  const [accHistoryPageData, setAccHistoryPageData] = useState([])
  const [historyRecords, setHistoryRecords] = useState(0)
  const [dataFetched, setDataFetched] = useState({
    accountDetails: false,
    initialHistory: false
  })

  const userId = accountDetailsData?.userId

  const paymentFilters = [
    { id: 1, name: 'DEPOSIT' },
    { id: 2, name: 'WITHDRAW' },
    { id: 0, name: 'BONUS' }
  ]

  const dateFilters = [
    {
      id: 0,
      name: 'Last 24hrs',
      formattedDate: moment().subtract(24, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      value: 'last24hrs'
    },
    {
      id: 1,
      name: 'Last Week',
      formattedDate: moment().subtract(1, 'week').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      value: 'lastWeek'
    },
    {
      id: 2,
      name: 'Last Month',
      formattedDate: moment().subtract(1, 'month').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      value: 'lastMonth'
    },
    {
      id: 3,
      name: 'Last 6months',
      formattedDate: moment().subtract(6, 'months').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      value: 'last6months'
    },
    {
      id: 4,
      name: 'Last Year',
      formattedDate: moment().subtract(1, 'year').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      value: 'lastYears'
    }
  ]

  useEffect(() => {
    if (token && !dataFetched.accountDetails) {
      dispatch(fetchAccountDetails(token))
      setDataFetched(prev => ({ ...prev, accountDetails: true }))
    }
  }, [token, dataFetched.accountDetails, dispatch])

  useEffect(() => {
    if (userId && !dataFetched.initialHistory && token) {
      if (selectedPaymentType === 'BONUS') {
        dispatch(fetchBonusHistory(userId, 0, token))
      } else {
        dispatch(fetchAccountHistory('DEPOSIT', 'last24hrs', 0, token))
      }
      setDataFetched(prev => ({ ...prev, initialHistory: true }))
    }
  }, [userId, dataFetched.initialHistory, selectedPaymentType, token, dispatch])

  const fetchHistoryData = useCallback(() => {
    if (userId && token) {
      if (selectedPaymentType === 'DEPOSIT' || selectedPaymentType === 'WITHDRAW') {
        dispatch(fetchAccountHistory(selectedPaymentType, selectedDurationType, currentPage - 1, token))
      } else if (selectedPaymentType === 'BONUS') {
        dispatch(fetchBonusHistory(userId, currentPage - 1, token))
      }
    }
  }, [userId, token, selectedPaymentType, selectedDurationType, currentPage, dispatch])

  useEffect(() => {
    if (dataFetched.initialHistory) {
      fetchHistoryData()
    }
  }, [currentPage, selectedPaymentType, selectedDurationType, dataFetched.initialHistory, fetchHistoryData])

  useEffect(() => {
    if (selectedPaymentType === 'DEPOSIT' || selectedPaymentType === 'WITHDRAW') {
      setAccHistoryPageData(accountHistoryData || [])
      setHistoryRecords(totalRecords || 0)
    } else if (selectedPaymentType === 'BONUS') {
      setAccHistoryPageData(bonusHistoryData?.data || [])
      setHistoryRecords(bonusHistoryData?.totalRecords || 0)
    }
  }, [selectedPaymentType, accountHistoryData, bonusHistoryData, totalRecords])

  const cancelRequest = () => {
    setIsCancel(!isCancel)
  }

  const cancelTransaction = () => {
    dispatch(cancelTransactionRequest(token))
    setIsCancel(false)
  }

  const handlePaymentFilter = paymentType => {
    if (paymentType === selectedPaymentType) return 
    
    setCurrentPage(1)
    setSelectedPaymentType(paymentType)
    
    if (paymentType === 'DEPOSIT' || paymentType === 'WITHDRAW') {
      setSelectedDurationType('last24hrs')
      dispatch(fetchAccountHistory(paymentType, 'last24hrs', 0, token))
    } else if (paymentType === 'BONUS' && userId) {
      dispatch(fetchBonusHistory(userId, 0, token))
    }
  }

  const handleDateFilter = durationType => {
    if (durationType === selectedDurationType) return
    
    setCurrentPage(1)
    setSelectedDurationType(durationType)
    dispatch(fetchAccountHistory(selectedPaymentType, durationType, 0, token))
  }

  const renderTransactionStatus = status => {
    let className = 'SB-badge '
    
    if (['CONFIRMED', 'VERIFIED', 'APPROVED'].includes(status)) {
      className += 'SB-success'
    } else if (['CANCELLED', 'DECLINED'].includes(status)) {
      className += 'SB-cancelled'
    } else if (status === 'HOLD') {
      className += 'SB-pending'
    } else if (status === 'INIT') {
      className += 'SB-initialized'
    } else if (status === 'CONFIRMED') {
      className += 'SB-confirmed'
    } else if (status === 'DECLINED') {
      className += 'SB-declined'
    } else if (status === 'FAILED') {
      className += 'SB-failed'
    }
    
    return <span className={className}>{status}</span>
  }

  const getTransactionAmount = transaction => {
    if (transaction.transactionType === "UNLOCKED_BONUS_APPLIED" || transaction.transactionType === "BONUS_UNLOCKED") {
      return transaction.unlockedBonus
    } else if (transaction.transactionType === "LOCKED_BONUS_APPLIED" || transaction.transactionType === "BONUS_LOCKED") {
      return transaction.lockedBonus
    } else {
      return transaction.amount || Math.abs(transaction.transactionAmount)
    }
  }

  const isLoading = loader || (selectedPaymentType === 'BONUS' && bonusLoader)

  return (
    <React.Fragment>
      <div className='SB-page-content'>
        <div className='SB-myAccountSection-container SB-transactionHistory'>
          <div className=''>
            <div className='SB-pageHeader'>
              <div className='SB-pageHeader-title'>Account History</div>
            </div>
            {isLoading ? (
              <div className='SB-section-loader'>
                <div className='SB-section-loader-container'>
                  <span className='SB-dot'></span>
                  <span className='SB-dot'></span>
                  <span className='SB-dot'></span>
                  <span className='SB-dot'></span>
                </div>
              </div>
            ) : (
              <>
                <div className='SB-filterDropdowns'>
                  <div className='SB-select SB-selectAuto'>
                    <select
                      value={selectedPaymentType}
                      onChange={e => handlePaymentFilter(e.target.value)}
                    >
                      {paymentFilters.map(filter => (
                        <option value={filter.name} key={filter.id}>
                          {filter.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedPaymentType !== 'BONUS' && (
                    <div className='SB-select SB-selectAuto'>
                      <select
                        value={selectedDurationType}
                        onChange={e => handleDateFilter(e.target.value)}
                      >
                        {dateFilters.map(filter => (
                          <option value={filter.value} key={filter.id}>
                            {filter.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className='SB-transactionBox'>
                  {accHistoryPageData?.length > 0 ? (
                    accHistoryPageData.map(transaction => (
                      <div className='SB-transaction-item' key={transaction.transactionId || transaction.id}>
                        <div className='SB-transaction__col SB-col1'>
                          <div className='SB-transaction-dateID'>
                            <div className='SB-transactionID'>
                              ID {transaction.id}
                            </div>
                            <div className='SB-transactionDate'>
                              {moment(transaction.createdDate).format('DD/MM/YYYY HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                        <div className='SB-transaction__col SB-col2'>
                          {selectedPaymentType === 'BONUS' ? (
                            <div className=''>
                              <div className='SB-transactionType'>
                                {transaction?.userBonusDetails?.bonusType}
                              </div>
                              <div className='SB-transactionMethod'>
                                {transaction.transactionType}
                              </div>
                            </div>
                          ) : transaction.manualAdjustment === "AFFILIATE_VIP_COMMISSION" && transaction.transactionRef === "MANUAL_PAYMENT" ? (
                            <div className='SB-transactionMethod'>
                              VIP_COMMISSION 
                            </div>
                          ) : (
                            <div className='SB-transactionMethod'>
                              {transaction.transactionRef} 
                            </div>
                          )}
                          <div className='SB-transaction-statusAmount'>
                            <div
                              className={`SB-transactionAmount ${
                                selectedPaymentType === 'WITHDRAW' ? 'SB-error' : 
                                selectedPaymentType === 'DEPOSIT' ? 'deposit' : ''
                              }`}
                            >
                              KES {getTransactionAmount(transaction)}
                            </div>
                            {renderTransactionStatus(transaction.status)}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='SB-msgBox'>
                      <div className='SB-msgBox-container'>
                        Currently there is no data available
                      </div>
                    </div>
                  )}
                </div>
                <PaginationComponent
                  currentPage={currentPage}
                  totalCount={historyRecords || 0}
                  pageSize={PageSize}
                  onPageChange={page => setCurrentPage(page)}
                  setCurrentPage={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
        {isCancel && (
          <div className={`SB-modal SB-confirmation-modal ${isCancel ? 'open' : 'hide'}`} id='SB-confirmation-modal'>
            <div className='SB-modal-dialog'>
              <div className='SB-modal-content'>
                <div className='SB-modal-close' onClick={cancelRequest} data-dismiss='modal'>
                  <div className='SB-iconSVG'>
                    <svg xmlns='http://www.w3.org/2000/svg'>
                      <use xlinkHref={`${icons}#${'icon-close'}`}></use>
                    </svg>
                  </div>
                </div>
                <div className='SB-modal-innerContent'>
                  <p className='text-center'>
                    Do you want to cancel transaction?
                  </p>
                </div>
                <div className='SB-modal-footer'>
                  <div className='SB-btnYesNoContainer'>
                    <button
                      className='btn SB-btnDefault active SB-btnMedium SB-btnNo'
                      id='cancelbtnNo'
                      onClick={cancelRequest}
                    >
                      No
                    </button>
                    <button
                      className='btn SB-btnSecondary active SB-btnMedium SB-btnYes'
                      id='cancelbtnYes'
                      onClick={cancelTransaction}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  )
}

export default AccountHistory